import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useUserDataFlattened } from "../../../modules/user/stores";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { ROUTES } from "@/types/routes";
import type { SevenDayRechargePopupResult } from "./useSevenDayRechargePopup.types";
import {
  useActivityPopupPriority,
  useIsActivityQualificationAutoEnabled,
  useDailyRechargeActivity,
  useIsHomeDisplayReady,
  useActivities,
  isActivityIdValid,
} from "@/modules/homeDisplay/hooks";
import { ActivityType, TimingType } from "@/modules/homeDisplay/types";
import { hasAutoClaimedJackpot } from "../components/SevenDayRecharge/SevenDayRecharge.utils";
import { isDepositPopupOpen } from "@/components/DepositPopup/triggerDepositPopup";

// 用于区分"可领取大奖"自动弹窗的存储key前缀
const CLAIMABLE_POPUP_KEY_PREFIX = "seven-day-recharge-claimable";

/**
 * 获取可领取大奖弹窗的记录key
 */
const getClaimablePopupKey = (userId: string): string => {
  return `${CLAIMABLE_POPUP_KEY_PREFIX}_${userId}`;
};

/**
 * 检查今天是否已经为可领取状态弹出过
 */
const hasShownClaimablePopupToday = (userId: string): boolean => {
  try {
    const records = localStorage.getItem("shown_popup_records");
    if (!records) return false;
    const parsed = JSON.parse(records) as Record<
      string,
      { time: string; timestamp: number }
    >;
    const key = getClaimablePopupKey(userId);
    if (!parsed[key]) return false;

    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    return parsed[key].time === todayString;
  } catch {
    return false;
  }
};

/**
 * 标记可领取大奖弹窗今天已弹出
 */
const markClaimablePopupShownToday = (userId: string): void => {
  try {
    const records = localStorage.getItem("shown_popup_records");
    const parsed = records
      ? (JSON.parse(records) as Record<
          string,
          { time: string; timestamp: number }
        >)
      : {};
    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const key = getClaimablePopupKey(userId);
    parsed[key] = { time: todayString, timestamp: Date.now() };
    localStorage.setItem("shown_popup_records", JSON.stringify(parsed));
  } catch {
    // ignore
  }
};

const SevenDayRecharge = lazy(() =>
  import("../components/SevenDayRecharge/SevenDayRecharge").then(m => ({
    default: m.SevenDayRecharge,
  }))
);

const SEVEN_DAY_RECHARGE_CONFIG = POPUP_CONFIGS.SEVEN_DAY_RECHARGE;

export const useSevenDayRechargePopup = (): SevenDayRechargePopupResult => {
  const { data: userInfo, userId } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();
  const activities = useActivities();

  const dailyRechargeActivities = useDailyRechargeActivity();
  const dailyRecharge = useMemo(
    () => dailyRechargeActivities[0],
    [dailyRechargeActivities]
  );

  // 判断所有小任务是否都已完成（不是大奖领取状态）
  // dailyRechargeAllCompleted = 所有周期里每一个小任务（28个）全部完成
  const dailyRechargeAllCompleted = useMemo(() => {
    if (!dailyRecharge?.dailyRechargeData) return false;

    const jackpotPeriods = dailyRecharge.dailyRechargeData.jackpotPeriods;

    // 如果有新的 jackpotPeriods 数据结构
    if (jackpotPeriods && jackpotPeriods.length > 0) {
      // 检查所有周期的所有小任务是否都已完成（claimed = true）
      return jackpotPeriods.every(period => {
        // 添加安全检查，确保 dailyRechargeItem 存在且是数组
        if (
          !period?.dailyRechargeItem ||
          !Array.isArray(period.dailyRechargeItem)
        ) {
          return false;
        }
        return period.dailyRechargeItem.every(item => item?.claimed === true);
      });
    }

    // 兼容旧数据结构
    return dailyRecharge.dailyRechargeData.dailyRechargeAllCompleted ?? false;
  }, [dailyRecharge?.dailyRechargeData]);

  // 判断是否开启循环参与（第二期）
  const isRecurringMode = useMemo(() => {
    const jackpotPeriods = dailyRecharge?.dailyRechargeData?.jackpotPeriods;
    return (jackpotPeriods?.length ?? 0) > 1;
  }, [dailyRecharge?.dailyRechargeData]);

  // 检测是否有系统自动领取的大奖（需要主动弹窗）
  const autoClaimedNode = useMemo(() => {
    return hasAutoClaimedJackpot(isRecurringMode, dailyRecharge);
  }, [isRecurringMode, dailyRecharge]);

  // 获取当前活跃周期（用于 badge 判断）
  const currentActivePeriod = useMemo(() => {
    if (!dailyRecharge?.dailyRechargeData) return null;

    const jackpotPeriods = dailyRecharge.dailyRechargeData.jackpotPeriods;
    if (!jackpotPeriods || jackpotPeriods.length === 0) return null;

    // 找到第一个未完成7天任务的周期，或第一个大奖未领取的周期
    for (const period of jackpotPeriods) {
      // 添加安全检查
      if (
        !period?.dailyRechargeItem ||
        !Array.isArray(period.dailyRechargeItem)
      ) {
        continue;
      }

      const completedCount = period.dailyRechargeItem.filter(
        item => item?.claimed === true
      ).length;
      // 如果该周期还没完成7天，或者7天完成了但大奖未领取，则为当前活跃周期
      if (completedCount < period.dailyRechargeItem.length || !period.claimed) {
        return period;
      }
    }

    // 所有周期都完成了，返回最后一个周期
    return jackpotPeriods[jackpotPeriods.length - 1] || null;
  }, [dailyRecharge]);
  // 获取活动配置中的 priority，不存在则使用默认值
  const priority = useActivityPopupPriority(
    ActivityType.DAILY_RECHARGE,
    SEVEN_DAY_RECHARGE_CONFIG.priority,
    TimingType.ACTIVITY_QUALIFICATION
  );
  const isAutoEnabled = useIsActivityQualificationAutoEnabled(
    ActivityType.DAILY_RECHARGE
  );

  // 检查活动是否真实存在于 activities 配置中
  const isActivityValid = useMemo(() => {
    return isActivityIdValid(
      dailyRecharge?.id,
      ActivityType.DAILY_RECHARGE,
      activities
    );
  }, [dailyRecharge?.id, activities]);

  const [badgeRefreshKey, setBadgeRefreshKey] = useState(0);

  const SevenDayRechargeContent: React.FC<{
    onComplete: () => void;
  }> = ({ onComplete }) => {
    const [visible, setVisible] = useState(true);

    const handleClose = () => {
      setVisible(false);
      onComplete();
      // 触发 badge 重新计算
      setBadgeRefreshKey(prev => prev + 1);
    };

    return (
      <Suspense fallback={null}>
        <SevenDayRecharge visible={visible} onClose={handleClose} />
      </Suspense>
    );
  };

  const shouldShowBadge = useMemo(() => {
    if (!dailyRecharge || !userId) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateKey = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}`;

    // 获取当前活跃周期的任务列表
    const jackpotPeriods = dailyRecharge.dailyRechargeData?.jackpotPeriods;
    let tasks = currentActivePeriod?.dailyRechargeItem;

    // 兼容旧数据结构
    if (!jackpotPeriods && dailyRecharge.dailyRechargeData?.dailyRechargeItem) {
      tasks = dailyRecharge.dailyRechargeData.dailyRechargeItem;
    }

    if (!tasks) return false;

    const popupKey = `seven-day-recharge_${userId}`;

    const getShownPopupRecords = () => {
      const records = localStorage.getItem("shown_popup_records");
      if (!records) return null;
      try {
        return JSON.parse(records) as Record<
          string,
          { time: string; timestamp: number }
        >;
      } catch {
        return null;
      }
    };

    const hasShownToday = () => {
      const records = getShownPopupRecords();
      if (!records || !records[popupKey]) return false;

      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const date = String(today.getDate()).padStart(2, "0");
      const todayString = `${year}-${month}-${date}`;

      return records[popupKey].time === todayString;
    };

    // 如果所有大奖都已领取
    if (dailyRechargeAllCompleted) {
      if (!hasShownToday()) {
        return true;
      }

      const animationKey = `seven_day_recharge_animation_${userId}_${dateKey}`;
      const hasClaimedToday = sessionStorage.getItem(animationKey);
      if (!hasClaimedToday) {
        return true;
      }

      return false;
    }

    // 检查是否有已完成的任务
    const paidTasks = tasks.filter(task => task?.claimed === true);
    const lastPaidTask =
      paidTasks.length > 0 ? paidTasks[paidTasks.length - 1] : null;

    if (lastPaidTask && lastPaidTask.paidTime) {
      const paidDate = new Date(lastPaidTask.paidTime);
      paidDate.setHours(0, 0, 0, 0);
      const isLastPaidToday = paidDate.getTime() === today.getTime();

      if (isLastPaidToday) {
        const animationKey = `seven_day_recharge_animation_${userId}_${dateKey}`;
        const hasClaimedToday = sessionStorage.getItem(animationKey);
        if (!hasClaimedToday) {
          return true;
        }
      } else {
        if (!hasShownToday()) {
          return true;
        }
      }
    } else {
      if (!hasShownToday()) {
        return true;
      }
    }

    return false;
  }, [
    dailyRecharge,
    dailyRechargeAllCompleted,
    userId,
    badgeRefreshKey,
    currentActivePeriod,
  ]);

  // 判断是否应该显示弹窗
  const shouldShow = useMemo(() => {
    if (!dailyRecharge?.dailyRechargeData) return false;

    const countdownSeconds =
      dailyRecharge.dailyRechargeData.countdownSeconds ?? 0;
    if (countdownSeconds <= 0) return false;

    const jackpotPeriods = dailyRecharge.dailyRechargeData.jackpotPeriods;

    // 如果有新的 jackpotPeriods 数据结构
    if (jackpotPeriods && jackpotPeriods.length > 0) {
      // 至少有一个周期的大奖未领取，就显示
      return jackpotPeriods.some(period => period && !period.claimed);
    }

    // 兼容旧数据结构
    return !dailyRecharge.dailyRechargeData.jackpotClaimed;
  }, [dailyRecharge]);

  // 用于追踪是否已触发可领取状态的弹窗
  const hasTriggeredClaimableRef = useRef(false);
  // 用于追踪是否已触发系统自动领取的弹窗
  const hasTriggeredAutoClaimRef = useRef(false);

  const { trigger } = useQueuedPopup({
    popupId: SEVEN_DAY_RECHARGE_CONFIG.id,
    priority,
    shouldShow: ctx => {
      // 充值弹窗开着时不自动插队：DepositPopup 是浮层、不改路由，下面 blockedPaths
      // 里的 ROUTES.RECHARGE 只挡充值「路由页」，挡不到它。手动触发放行 —— 那是
      // 用户自己点的入口，拦掉会变成「点了没反应」。
      if (!ctx?.isManualTrigger && isDepositPopupOpen()) return false;
      return isConfigReady && !!dailyRecharge && isActivityValid;
    },
    renderContent: onComplete => (
      <SevenDayRechargeContent onComplete={onComplete} />
    ),
    showOnce: true,
    allowManualTrigger: true,
    autoTrigger: isAutoEnabled,
    dependencies: [isConfigReady, dailyRecharge, isActivityValid, isAutoEnabled],
    allowedPaths: [ROUTES.HOME, ROUTES.HOME_ALT, "/", ROUTES.LOGIN, ROUTES.REGISTER],
    blockedPaths: [
      "/game",
      ROUTES.RECHARGE,
      ROUTES.LOGIN,
      ROUTES.REGISTER,
      ROUTES.WITHDRAW,
    ],
    userBinding: true,
    getUserId: () => userInfo?.userId,
    timingType: TimingType.ACTIVITY_QUALIFICATION,
  });

  // 当检测到可领取大奖状态时，自动弹出弹窗（区分普通进入时的弹窗）
  const triggerClaimablePopup = useCallback(() => {
    if (!userId) return;
    // 检查今天是否已经为可领取状态弹出过
    if (hasShownClaimablePopupToday(userId)) return;
    // 标记已弹出，防止重复弹出
    markClaimablePopupShownToday(userId);
    hasTriggeredClaimableRef.current = true;
    // 触发弹窗
    trigger();
  }, [userId, trigger]);

  // 监听可领取大奖状态变化，自动弹出
  useEffect(() => {
    // 如果已经在本次会话触发过，不再重复
    if (hasTriggeredClaimableRef.current) return;
    // 检查活动是否还在进行中
    const countdownSeconds =
      dailyRecharge?.dailyRechargeData?.countdownSeconds ?? 0;
    if (countdownSeconds <= 0) return;
    // 只有在可领取大奖且用户已登录时才触发
    if (dailyRechargeAllCompleted && userId && dailyRecharge) {
      triggerClaimablePopup();
    }
  }, [dailyRechargeAllCompleted, userId, dailyRecharge, triggerClaimablePopup]);

  // 当数据变化（如领取后）重置触发标记
  useEffect(() => {
    if (!dailyRechargeAllCompleted) {
      hasTriggeredClaimableRef.current = false;
    }
  }, [dailyRechargeAllCompleted]);

  // 监听系统自动领取状态，当天第一次登录时主动弹窗
  useEffect(() => {
    // 如果已经在本次会话触发过，不再重复
    if (hasTriggeredAutoClaimRef.current) return;
    // 如果不是系统自动领取场景，不触发
    if (!autoClaimedNode) return;
    // 如果用户未登录，不触发
    if (!userId) return;
    // 检查活动是否还在进行中
    const countdownSeconds =
      dailyRecharge?.dailyRechargeData?.countdownSeconds ?? 0;
    if (countdownSeconds <= 0) return;

    // 检查今天是否已经为系统自动领取弹出过
    const AUTO_CLAIM_POPUP_KEY = `seven-day-auto-claim_${userId}`;
    const hasShownAutoClaimToday = () => {
      try {
        const records = localStorage.getItem("shown_popup_records");
        if (!records) return false;
        const parsed = JSON.parse(records) as Record<
          string,
          { time: string; timestamp: number }
        >;
        if (!parsed[AUTO_CLAIM_POPUP_KEY]) return false;

        const today = new Date();
        const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
        return parsed[AUTO_CLAIM_POPUP_KEY].time === todayString;
      } catch {
        return false;
      }
    };

    if (hasShownAutoClaimToday()) return;

    // 标记已弹出，防止重复弹出
    try {
      const records = localStorage.getItem("shown_popup_records");
      const parsed = records
        ? (JSON.parse(records) as Record<
            string,
            { time: string; timestamp: number }
          >)
        : {};
      const today = new Date();
      const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      parsed[AUTO_CLAIM_POPUP_KEY] = {
        time: todayString,
        timestamp: Date.now(),
      };
      localStorage.setItem("shown_popup_records", JSON.stringify(parsed));
    } catch {
      // ignore
    }

    hasTriggeredAutoClaimRef.current = true;
    // 触发弹窗
    trigger();
  }, [autoClaimedNode, userId, trigger]);

  // 当系统自动领取节点不存在时，重置触发标记
  useEffect(() => {
    if (!autoClaimedNode) {
      hasTriggeredAutoClaimRef.current = false;
    }
  }, [autoClaimedNode]);

  const remainingMs =
    Math.max(0, dailyRecharge?.dailyRechargeData?.countdownSeconds ?? 0) * 1000;

  return {
    shouldShow,
    trigger,
    remainingMs,
    shouldShowBadge,
    landingPageTitle: dailyRecharge?.displayConfig?.landingPageTitle,
  };
};
